import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import isDifferent from 'deep-diff';
import { yupResolver } from '@hookform/resolvers';
import { useLocation } from 'react-router-dom';
import { getDevice } from '@panel/commons/helpers/device';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import { ImageSection, PlansSection } from './images/index';
import { SavePostingSelectors, SavePostingOperations } from '../../../ducks/savePosting';
import { Videos, Videos360 } from './videos/index';
import { formSchema, videosSchema } from './validations';
import {
  getMultimedias,
  validate,
  stepData,
  getMultimediaState,
  getSavedMedia,
  cleanMultimedia,
  MultimediaCollection,
  validateVideo360State,
  getPreviewStorageLoading,
  getValidPictures,
  getValidPlans,
  getPictures,
  getPlans,
  getVideos,
  getVideos360,
  cleanGetMultimedia
} from '../../../ducks/multimedia';
import Section from '../../commons/AccordionSection';
import { OptionalSections } from './styles';
import useFormIsDirty from '../../../hooks/useFormIsDirty';

const StepMultimedia = () => {
  const device = getDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const [validating, setValidating] = useState(false);
  const { setFormIsDirty } = useFormIsDirty();
  const [validateVideo, setValidateVideo] = useState(false);
  const [validate360, setValidate360] = useState(false);
  const { ...validationData } = useSelector(validate);
  const { ...formData } = useSelector(stepData);
  const { originalMultimedia } = useSelector(getSavedMedia);
  const { getMultimediaLoading, getMultimediaError, getMultimediaCompleted } = useSelector(getMultimediaState);
  const { images: imagesValid } = useSelector(getValidPictures); // Usamos para validar las images sin error y con temporalUrl. Para poder comparar con lo valores originales.
  const { plans: plansValid } = useSelector(getValidPlans); // Usamos para validar los plans sin error y con temporalUrl. Para poder comparar con lo valores originales.
  const { images: imagesEdited } = useSelector(getPictures);
  const { plans: plansEdited } = useSelector(getPlans);
  const { videos } = useSelector(getVideos);
  const { tour360 } = useSelector(getVideos360);
  const { saving } = useSelector(SavePostingSelectors.getSavePostingState);
  const { validation360Loading } = useSelector(validateVideo360State);
  const previewStorageLoading = useSelector(getPreviewStorageLoading);
  const { snackAction, currentStep } = useSelector(StepperSelectors.getStepperState);

  const {
    register,
    errors,
    trigger,
    setValue,
    getValues,
    formState,
    handleSubmit,
    watch,
    clearErrors,
    setError
  } = useForm({
    resolver: yupResolver(videosSchema),
    context: {
      validateVideo,
      validate360,
      videos
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    shouldFocusError: true
  });

  const originalImages = originalMultimedia?.pictures || [];
  const originalPlans = originalMultimedia?.plan || [];
  const originalVideos = originalMultimedia?.videos || [];
  const originalTour360 = originalMultimedia?.tour360 || [];

  const checkMediaChangeInEdition = () => {
    // Caso cuando es un DRAFT y aún no se han cargado archivos multimedia:
    if (
      !originalMultimedia &&
      (imagesValid?.length > 0 || plansValid?.length > 0 || videos?.length > 0 || tour360?.length > 0)
    ) {
      return true;
    }

    if (originalMultimedia) {
      const compareFields = (arr1, arr2) => {
        // Si son idénticos isDifferent retorna undefined
        return isDifferent(arr1, arr2) !== undefined;
      };
      const hasImageChanges = compareFields(imagesValid, originalImages);
      const hasPlanChanges = compareFields(plansValid, originalPlans);
      const hasVideoChanges = compareFields(videos, originalVideos);
      const hasTourChanges = compareFields(tour360, originalTour360);

      return hasImageChanges || hasPlanChanges || hasVideoChanges || hasTourChanges;
    }

    return false;
  };

  const mediaChangeInEdition = checkMediaChangeInEdition();

  // Snackbar de error para validaciones:
  const showErrorSnackbar = (actionKey, message) => {
    dispatch(
      StepperOperations.setSnackAction({
        action: actionKey,
        result: 'ERROR',
        component: message,
        section: 'Publicador profesionales - Multimedia',
        preventDuplicateActions: true
      })
    );
  };

  const onSubmit = async () => {
    try {
      await formSchema.validate(validationData);
      const payload = {
        body: { ...formData, postingId },
        step: 'STEP_MULTIMEDIA'
      };
      dispatch(SavePostingOperations.savePostingDB(payload));
      dispatch(cleanMultimedia(MultimediaCollection.IMAGE));
      dispatch(cleanMultimedia(MultimediaCollection.PLAN));
    } catch (validationError) {
      if (validationError) {
        showErrorSnackbar('EmptyError', t(validationError.errors[0]));
      }
      // Seteamos estado de validación para mostrar color de error en caso de errorLimitMin en imagenes
      setValidating(true);
    }
  };

  usePreventWindowUnload(formState.isDirty || mediaChangeInEdition);

  // Se actualiza el formulario con los datos recibidos si tenemos postingId:
  useEffect(() => {
    if (postingId && !getMultimediaError && !getMultimediaCompleted) {
      dispatch(getMultimedias(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, getMultimediaError, getMultimediaCompleted]);

  // Refrescamos datos cuando nos devolvemos a multimedia
  useEffect(() => {
    if (getMultimediaCompleted) {
      dispatch(cleanGetMultimedia()); // Esto resetea getMultimediaCompleted a false para que se pueda volver a hacer el getMultimedias
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Efecto para setear estado del modal de salir por acciones del menú cuando hay cambios
  useEffect(() => {
    if (mediaChangeInEdition) {
      setFormIsDirty(formState.isDirty || mediaChangeInEdition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty, mediaChangeInEdition, previewStorageLoading]);

  // Saneamiento del state que dispara el Snackbar:
  useEffect(() => {
    return () => {
      dispatch(StepperOperations.setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  // Función para detectar si el formulario es válido y cambiar la traducción del modal de Guardar y Salir
  const getIsValidForm = async () => {
    // Se valida el esquema de límite de archivos, devuelve true si pasa las validaciones
    const isSchemaValid = await formSchema.isValid(validationData);
    // Verificamos si hay imágenes con errores o sin temporalUrl
    const hasImageErrors = imagesEdited?.some(image => image.error !== null || image.temporalUrl === undefined);
    // Verificamos si hay planos con errores
    const hasPlanErrors = plansEdited?.some(plan => plan.error !== null);
    // Validamos si no hay errores en los videos y videos360
    const noErrors = Object.keys(errors)?.length === 0;
    return isSchemaValid && noErrors && !hasImageErrors && !hasPlanErrors;
  };

  return (
    <WrapperContainer
      id='multimedia-step'
      getIsValidForm={getIsValidForm}
      // Props para el title y note del Step:
      title={t('professionalPubli.multimedia.title')}
      note={t('professionalPubli.multimedia.note')}
      // Props para el ButtonPanel:
      onSubmit={handleSubmit(onSubmit)}
      btnSubmitText={t('professionalPubli.cta.continue')}
      formValuesLoading={getMultimediaLoading || validation360Loading}
      loadError={getMultimediaError}
      tips={!getMultimediaLoading && [t('professionalPubli.tip.multimedia')]}
      disabledSubmit={previewStorageLoading}
    >
      <form data-publi='multimedia-Form' id='multimedia-step' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <fieldset data-publi='multimedia-fieldset' disabled={saving}>
          <ImageSection
            data-publi='multimedia-imagesSection'
            device={device}
            postingId={postingId}
            showErrorSnackbar={showErrorSnackbar}
            validating={validating}
            setValidating={setValidating}
          />
          <OptionalSections>
            <div className='optinalSection'>{t('professionalPubli.multimedia.moreOptions')}</div>
            <Section
              dataPubli='multimedia-videos'
              device={device}
              title={t('professionalPubli.multimedia.video.title')}
              externalActive={originalVideos.length > 0}
            >
              <Videos
                data-publi='multimedia-videos'
                register={register}
                errors={errors}
                setValue={setValue}
                trigger={trigger}
                getValues={getValues}
                setValidateVideo={setValidateVideo}
                validateVideo={validateVideo}
                clearErrors={clearErrors}
                device={device}
                watch={watch}
              />
            </Section>
            <Section
              dataPubli='multimedia-plans'
              device={device}
              title={t('professionalPubli.multimedia.plans.title')}
              externalActive={originalPlans.length > 0}
            >
              <PlansSection
                data-publi='multimedia-plansSection'
                device={device}
                postingId={postingId}
                showErrorSnackbar={showErrorSnackbar}
              />
            </Section>
            <Section
              dataPubli='multimedia-video360'
              device={device}
              title={t('professionalPubli.multimedia.video360.title')}
              externalActive={originalTour360.length > 0}
            >
              <Videos360
                data-publi='multimedia-video360'
                register={register}
                errors={errors}
                setValue={setValue}
                trigger={trigger}
                getValues={getValues}
                setValidateVideo={setValidate360}
                validateVideo={validate360}
                clearErrors={clearErrors}
                watch={watch}
                setError={setError}
                device={device}
                showErrorSnackbar={showErrorSnackbar}
              />
            </Section>
          </OptionalSections>
        </fieldset>
      </form>
    </WrapperContainer>
  );
};

export default StepMultimedia;
